import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { PlanProvider } from "@/lib/plan";
import { Toaster } from "sonner";
import LoginPage from "@/pages/LoginPage";
import SettingsPage from "@/pages/SettingsPage";
import FirstLoginPage from "@/pages/FirstLoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AdminUsersPage from "@/pages/AdminUsersPage";
import AROverTimePage from "@/pages/AROverTimePage";
import AuditLogPage from "@/pages/AuditLogPage";
import UploadPage from "@/pages/UploadPage";
import DashboardPage from "@/pages/DashboardPage";
import ExportsPage from "@/pages/ExportsPage";
import ScorecardsPage from "@/pages/ScorecardsPage";
import WorkQueuePage from "@/pages/WorkQueuePage";
import AnsiCodeLibraryPage from "@/pages/AnsiCodeLibraryPage";
import SamplePlaybookPage from "@/pages/SamplePlaybookPage";

const Protected = ({ children, requireRole }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="text-slate-400 text-sm">Loading...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // Force first-login password change. /first-login is the only page allowed.
  if (user.must_change_password && location.pathname !== "/first-login") {
    return <Navigate to="/first-login" replace />;
  }
  if (requireRole && user.role !== requireRole) {
    return <Navigate to="/dashboard" replace />;
  }
  // Platform admins have no organization — they can ONLY use /admin/* and /settings.
  // Redirect any other route to the admin console.
  const adminAllowedPrefixes = ["/admin", "/settings", "/first-login"];
  if (user.role === "admin" && !user.organization_id && !adminAllowedPrefixes.some((p) => location.pathname.startsWith(p))) {
    return <Navigate to="/admin/users" replace />;
  }
  return children;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <PlanProvider>
            <Toaster position="top-right" richColors closeButton />
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/sample-playbook" element={<SamplePlaybookPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/first-login" element={<Protected><FirstLoginPage /></Protected>} />
              <Route path="/dashboard" element={<Protected><DashboardPage /></Protected>} />
              <Route path="/ar-over-time" element={<Protected><AROverTimePage /></Protected>} />
              <Route path="/upload" element={<Protected><UploadPage /></Protected>} />
              <Route path="/scorecards" element={<Protected><ScorecardsPage /></Protected>} />
              <Route path="/workqueue" element={<Protected><WorkQueuePage /></Protected>} />
              <Route path="/ansi-codes" element={<Protected><AnsiCodeLibraryPage /></Protected>} />
              <Route path="/exports" element={<Protected><ExportsPage /></Protected>} />
              <Route path="/settings" element={<Protected><SettingsPage /></Protected>} />
              <Route path="/admin/users" element={<Protected requireRole="admin"><AdminUsersPage /></Protected>} />
              <Route path="/admin/audit-log" element={<Protected requireRole="admin"><AuditLogPage /></Protected>} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </PlanProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
